import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { SplitClient, SplitFactoryProvider } from '@splitsoftware/splitio-react';
import {
  User,
  HarnessStartupAttributes,
  HarnessPlatform,
  ANONYMOUS_FEATURE_FLAGS_FALLBACK_KEY
} from '@barracuda/shared';
import {
  DEFAULT_ENV,
  DEFAULT_READY_TIMEOUT_SECONDS,
  FEATURE_FLAGS_MODE_MESSAGES,
  FeatureFlagsMode,
  LOCALHOST_AUTHORIZATION_KEY,
  getAnonymousId,
  resolveFeatureFlagsIdentityKey,
  resolveFeatureFlagsMode
} from './HarnessFeatureFlagsProviderUtils';

interface HarnessFeatureFlagsProviderProps extends PropsWithChildren {
  isLoggedIn: boolean;
  user?: Pick<User, 'externalId' | 'email' | 'countryOfResidence'>;
}

export const HarnessFeatureFlagsProvider = ({
  children,
  isLoggedIn,
  user
}: HarnessFeatureFlagsProviderProps) => {
  const sdkKey = process.env.REACT_APP_HARNESS_FME_CLIENT_SDK_KEY;
  const mode = resolveFeatureFlagsMode(sdkKey, process.env.REACT_APP_LOCAL === 'true');
  const isLocalhostMode = !sdkKey;
  const hasExternalId = Boolean(user?.externalId);

  const [anonymousId, setAnonymousId] = useState<string>(ANONYMOUS_FEATURE_FLAGS_FALLBACK_KEY);

  useEffect(() => {
    let cancelled = false;
    getAnonymousId().then((deviceId) => {
      if (!cancelled) {
        setAnonymousId(deviceId);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const identityKey = resolveFeatureFlagsIdentityKey({
    isLoggedIn,
    anonymousId,
    externalId: user?.externalId
  });

  const attributes = useMemo(() => {
    const baseAttributes: HarnessStartupAttributes = {
      env: process.env.REACT_APP_MODE ?? DEFAULT_ENV,
      platform: HarnessPlatform.WEB,
      isLoggedIn,
      externalId: user?.externalId ?? ANONYMOUS_FEATURE_FLAGS_FALLBACK_KEY
    };

    if (user?.countryOfResidence?.isoCode) {
      baseAttributes.countryOfResidence = user.countryOfResidence.isoCode;
    }

    return baseAttributes;
  }, [isLoggedIn, user]);

  const splitConfig = useMemo<SplitIO.IBrowserSettings>(
    () => ({
      core: {
        authorizationKey: isLocalhostMode ? LOCALHOST_AUTHORIZATION_KEY : (sdkKey as string),
        key: anonymousId
      },
      startup: { readyTimeout: DEFAULT_READY_TIMEOUT_SECONDS },
      streamingEnabled: true
    }),
    [anonymousId, isLocalhostMode, sdkKey]
  );

  useEffect(() => {
    console.info('[HarnessFeatureFlagsProvider] Initializing feature flags provider', {
      mode,
      hasSdkKey: Boolean(sdkKey),
      hasExternalId,
      usingAnonymousIdentity: identityKey === anonymousId,
      identityKey,
      environment: process.env.REACT_APP_MODE ?? DEFAULT_ENV,
      attributes
    });

    if (mode === FeatureFlagsMode.FALLBACK) {
      console.warn(FEATURE_FLAGS_MODE_MESSAGES[mode]);
    }
  }, [anonymousId, hasExternalId, identityKey, mode, sdkKey, attributes]);

  return (
    <SplitFactoryProvider config={splitConfig} attributes={attributes}>
      <SplitClient splitKey={identityKey} attributes={attributes}>
        {children}
      </SplitClient>
    </SplitFactoryProvider>
  );
};
